<script>
import CartData from "@/components/CartData.vue";
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from "vue";
import { useMenuStore } from "@/stores/menu";
import { useCartStore } from "@/stores/cart";
import { useTranslationStore } from "@/stores/translation";
import { formatDishPrice } from "@/utils/price";
import { useSEO } from "@/composables/useSEO";

import { storeToRefs } from "pinia";

export default {
  components: {
    CartData,
  },
  setup() {
    const menuStore = useMenuStore();
    const cartStore = useCartStore();
    const translationStore = useTranslationStore();

    // Translation Helper
    const t = (label, modules, fallback) => {
      try {
        return translationStore.translate(label, modules, fallback);
      } catch (error) {
        console.error('Error in translation:', error);
        return label || '';
      }
    };

    const {
      items,
      cart,
      selectedItem,
      selectedCategory,
      cartItemCount,
      loading,
      isAllItemsLoaded,
      categories,
      categoriesLoading,
      categoriesError,
    } = storeToRefs(menuStore);

    const {
        totalQty
    } = storeToRefs(cartStore);

    // SEO Meta Tags with Open Graph, Twitter Cards, and Canonical URL
    useSEO({
      title: computed(() => t('seo_title', ['ordernow']) || 'Order Now - Chi Hun Da Su'),
      description: computed(() => t('seo_description', ['ordernow']) || 'Browse our complete menu and order delicious Chinese food from Chi Hun Da Su. Fast delivery available.'),
      keywords: computed(() => t('seo_keywords', ['ordernow']) || 'order Chinese food, online food delivery, menu, Chi Hun Da Su'),
      image: computed(() => {
        // Use first menu item image if available
        if (items.value && items.value.length > 0) {
          const firstItem = items.value[0];
          if (firstItem && firstItem.item_images) {
            const mainImage = firstItem.item_images.find((img) => img.is_main);
            if (mainImage) {
              return `https://api.chds.com.au${mainImage.image}`;
            }
          }
        }
        return "https://chds.com.au/og-default.jpg";
      }),
      type: "website",
      path: "/ordernow",
    });

    const searchQuery = ref("");
    const quantity = ref(1);
    
    // Store modal event listener reference for cleanup
    // const modalEventListener = ref(null);

    // Track initial loading state. Only the FIRST load shows the full-screen
    // loader; later category/search changes keep the layout mounted so the
    // (mobile) category strip doesn't remount and lose its scroll position.
    const initialLoaded = ref(false);
    const isInitialLoading = computed(() => {
      return (categoriesLoading.value || (loading.value && items.value.length === 0 && !initialLoaded.value));
    });

    const performSearch = () => {
      menuStore.searchItems(searchQuery.value);
    };

    // Live search-as-you-type — debounced so we don't fire an API call on every keystroke.
    let _searchTimer = null;
    watch(searchQuery, () => {
      clearTimeout(_searchTimer);
      _searchTimer = setTimeout(() => performSearch(), 350);
    });

    const handleSearchInput = (event) => {
      if (event.key === 'Enter') {
        performSearch();
      }
    };

    const handleSearchBlur = () => {
      performSearch();
    };

    const handleSearchClick = () => {
      performSearch();
    };

    const getItemImage = (item) => {
      try {
        if (!item || !item.item_images) return "";
        const mainImage = item.item_images.find((img) => img.is_main);
        const path = mainImage ? mainImage.image : "";
        return "https://api.chds.com.au" + path;
      } catch (error) {
        console.error('Error in getItemImage:', error);
        return "";
      }
    };

    // Focal point so the card image crops around the right part of the dish.
    const getItemFocal = (item) => {
      if (!item || !item.item_images) return "center";
      const main = item.item_images.find((img) => img.is_main) || item.item_images[0];
      if (!main || main.focal_x == null) return "center";
      return `${main.focal_x}% ${main.focal_y}%`;
    };

    const incrementCartItem = (item) => {
      try {
        menuStore.updateCartItemQuantity(item, item.quantity + 1);
      } catch (error) {
        console.error('Error in incrementCartItem:', error);
      }
    };

    const decrementCartItem = (item) => {
      try {
        if (item.quantity > 1) {
          menuStore.updateCartItemQuantity(item, item.quantity - 1);
        } else {
          menuStore.removeFromCart(item);
        }
      } catch (error) {
        console.error('Error in decrementCartItem:', error);
      }
    };

    const openItemModal = (item) => {
      try {
        menuStore.updateSelectedItem(item);
      } catch (error) {
        console.error('Error in openItemModal:', error);
      }
    };

    const incrementQuantity = () => {
      try {
        quantity.value += 1;
      } catch (error) {
        console.error('Error in incrementQuantity:', error);
      }
    };

    const decrementQuantity = () => {
      try {
        if (quantity.value > 1) {
          quantity.value -= 1;
        }
      } catch (error) {
        console.error('Error in decrementQuantity:', error);
      }
    };

    const addItemToCart = () => {
      try {
        if (selectedItem.value) {
          menuStore.addToCart(selectedItem.value, quantity.value);
          quantity.value = 1;
          menuStore.updateSelectedItem(null);
          
          // Show success toast
          showSuccessToast();
        }
      } catch (error) {
        console.error('Error in addItemToCart:', error);
      }
    };

    // Toast functionality
    const showSuccessToast = () => {
      try {
        if (window.bootstrap) {
          const toastElement = document.getElementById('successToast');
          if (toastElement) {
            const toast = new window.bootstrap.Toast(toastElement);
            toast.show();
          }
        }
      } catch (error) {
        console.error('Error showing toast:', error);
      }
    };

    // Infinite Scroll Logic
    const handleScroll = () => {
      try {
        // Find the footer element
        const footerElement = document.querySelector('.footer-section');
        
        if (footerElement) {
          // Get footer's position relative to the viewport
          const footerRect = footerElement.getBoundingClientRect();
          // Trigger loading when footer is about to come into view (100px before it appears)
          const footerIsApproaching = footerRect.top <= window.innerHeight + 100;
          
          if (footerIsApproaching && !isAllItemsLoaded.value && !loading.value) {
            menuStore.loadItems();
          }
        } else {
          // Fallback to original logic if footer not found
          const bottomOfWindow =
            window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
          if (bottomOfWindow && !isAllItemsLoaded.value && !loading.value) {
            menuStore.loadItems();
          }
        }
      } catch (error) {
        console.error('Error in handleScroll:', error);
      }
    };

    // Method to truncate description
    const truncateDescription = (text, maxLength=60) => {
      try {
        if (!text) return "";
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
      } catch (error) {
        console.error('Error in truncateDescription:', error);
        return "";
      }
    };

    const handleCategoryChange = (category) =>{
      try {
        menuStore.selectCategory(category);
      } catch (error) {
        console.error('Error in handleCategoryChange:', error);
      }
    }

    // Pick a fitting FontAwesome icon for a category by matching its name.
    const catIcon = (name) => {
      const n = (name || "").toLowerCase();
      if (/power|bowl/.test(n)) return "fas fa-bowl-food";
      if (/chicken|poultry/.test(n)) return "fas fa-drumstick-bite";
      if (/beef|pork|lamb|meat/.test(n)) return "fas fa-drumstick-bite";
      if (/seafood|fish|shrimp|prawn/.test(n)) return "fas fa-fish";
      if (/veg|salad|green/.test(n)) return "fas fa-leaf";
      if (/soup|broth/.test(n)) return "fas fa-mug-hot";
      if (/onigiri|rice|sushi/.test(n)) return "fas fa-bowl-rice";
      if (/dessert|sweet|cake|ice/.test(n)) return "fas fa-ice-cream";
      if (/combo|value|set|meal/.test(n)) return "fas fa-box-open";
      if (/appet|starter|snack/.test(n)) return "fas fa-pepper-hot";
      if (/main|course/.test(n)) return "fas fa-utensils";
      return "fas fa-utensils";
    };

    // Keep the search + category chips stuck just below the sticky navbar.
    // Default to a safe fallback so the bar never sticks at top:0 (behind the
    // z-index:99 navbar, which reads as "not sticky" on mobile). Only overwrite
    // once we have a real, non-zero navbar height.
    const stickyTop = ref(64);
    const measureNav = () => {
      const nav = document.querySelector(".navbar");
      const h = nav ? Math.round(nav.getBoundingClientRect().height) : 0;
      if (h > 0) stickyTop.value = h;
    };

    onMounted(async () => {
      try {
        measureNav();
        // Re-measure after the navbar logo image loads (its height grows once loaded).
        setTimeout(measureNav, 300);
        setTimeout(measureNav, 1000);
        window.addEventListener("load", measureNav);
        window.addEventListener("resize", measureNav);
        // Clear any previous search/category filters so loadItems fetches the full menu.
        menuStore.resetItems();
        menuStore.searchQuery = "";
        menuStore.selectedCategory = "";

        await menuStore.fetchCoupons();
        await menuStore.loadCategories();
        await menuStore.loadItems();
        // Load the rest of the menu in the background so no dish is "missing" on a later
        // page until the user scrolls (the whole catalogue is small). First page paints
        // immediately; the remainder fills in within a second.
        menuStore.loadAllRemaining();

        // Ensure we display the full unfiltered menu on initial mount.
        // This prevents leftover filtered results (from a previous search)
        // being shown when navigating back to the OrderNow page.
        if (menuStore.allMenuItems && menuStore.allMenuItems.length > 0) {
          menuStore.items = [...menuStore.allMenuItems];
        }

        // First load is done — subsequent category/search changes keep the
        // layout mounted (no full-screen loader, no chip-scroll reset).
        initialLoaded.value = true;

        window.addEventListener("scroll", handleScroll);

        // Auto-open the dish modal when arriving from a homepage card click. The global
        // AddItem modal (App.vue) watches menuStore.selectedItem, so we look the dish up
        // by id and set it — the watcher opens the modal on THIS page.
        // Open the dish modal after arriving from a homepage card. The full dish is passed
        // via the store (menuStore.pendingOpenItem), so this works for EVERY dish — even ones
        // not in this page's first loaded batch.
        if (menuStore.pendingOpenItem) {
          const pending = menuStore.pendingOpenItem;
          menuStore.pendingOpenItem = null;
          menuStore.updateSelectedItem(null);
          await nextTick();
          setTimeout(() => menuStore.updateSelectedItem(pending), 250);
        }
        
      } catch (error) {
        console.error('Error in OrderNow onMounted:', error);
      }
    });

    onBeforeUnmount(() => {
      try {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", measureNav);
        window.removeEventListener("load", measureNav);
      } catch (error) {
        console.error('Error in onBeforeUnmount:', error);
      }
    });

    return {
      t,
      stickyTop,
      items,
      selectedItem,
      cart,
      totalQty,
      searchQuery,
      performSearch,
      handleSearchInput,
      handleSearchBlur,
      handleSearchClick,
      formatDishPrice,
      getItemImage,
      getItemFocal,
      addToCart: menuStore.addToCart,
      incrementCartItem,
      decrementCartItem,
      cartItemCount,
      loading,
      isAllItemsLoaded,
      openItemModal,
      quantity,
      incrementQuantity,
      decrementQuantity,
      addItemToCart,
      truncateDescription,
      categories,
      categoriesLoading,
      categoriesError,
      CartData,
      selectedCategory,
      handleCategoryChange,
      catIcon,
      isInitialLoading,
      showSuccessToast,
    };
  },
};
</script>


<template>
  <!-- Loading Screen for Initial Load -->
  <div v-if="isInitialLoading" class="simple-loading-screen">
    <div class="simple-loading-content">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <h5>{{t('loading_menu', ['ordernow'] ) || 'Loading Menu...'}}</h5>
    </div>
  </div>

  <!-- Main Content -->
  <div v-else class="container-xxl">
    <div class="row">
      <!-- Category sidebar (desktop / tablet-large) -->
      <div class="col-xxl-2 col-xl-3 col-lg-3 d-none d-lg-block p-3 pe-lg-2">
        <div class="cat-sidebar">
          <h1 class="visually-hidden">Order healthy Chinese meals online — CHDS meal delivery Melbourne</h1>
            <div class="cat-title">{{ t('menu_categories', ['ordernow'], 'Menu Categories') }}</div>
          <nav class="cat-nav">
            <button
              class="cat-link"
              :class="{ active: selectedCategory === '' }"
              @click="handleCategoryChange('')"
            >
              <span class="cat-ic"><i class="fas fa-utensils"></i></span>
              <span class="cat-name">{{ t('all_chds_food', ['ordernow']) || 'All CHDS Food' }}</span>
            </button>
            <button
              v-for="category in categories"
              :key="category.id"
              class="cat-link"
              :class="{ active: selectedCategory === category.id }"
              @click="handleCategoryChange(category.id)"
            >
              <span class="cat-ic"><i :class="catIcon(category.name)"></i></span>
              <span class="cat-name">{{ category.name }}</span>
            </button>
          </nav>
        </div>
      </div>

      <!-- Menu Items -->
      <div
        class="col-xxl-8 col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12 p-3"
      >
        <div class="menu-items">
          <div class="menu-sticky-top" :style="{ top: stickyTop + 'px' }">
          <div class="row">
            <div class="col-12 p-2 pt-0">
              <div class="outlet-card p-2">
                <div class="input-group">
                  <input
                    class="form-control"
                    type="search"
                    :placeholder="t('search_dish', ['ordernow'], 'Search Dish')"
                    aria-label="Search"
                    v-model="searchQuery"
                    @keyup="handleSearchInput"
                    @blur="handleSearchBlur"
                  />
                  <button
                    class="btn btn-outline-secondary search-btn"
                    type="button"
                    @click="handleSearchClick"
                  >
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Category chips (mobile / small screens) -->
          <div class="cat-chips d-lg-none">
            <button
              class="chip"
              :class="{ active: selectedCategory === '' }"
              @click="handleCategoryChange(''); $event.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })"
            >{{ t('all_chds_food', ['ordernow']) || 'All' }}</button>
            <button
              v-for="category in categories"
              :key="'chip-' + category.id"
              class="chip"
              :class="{ active: selectedCategory === category.id }"
              @click="handleCategoryChange(category.id); $event.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })"
            >{{ category.name }}</button>
          </div>
          </div>

          <!-- Items List -->
          <div class="row p-0">
            <div
              class="col-xxl-4 col-xl-6 col-md-6 col-sm-12 col-12 p-2"
              v-for="item in items"
              :key="item.id"
            >
              <div
                class="card-item"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#exampleModal"
                @click="openItemModal(item)"
              >
                <div class="card-item-food p-3">
                  <div class="food-item-detail">
                    <h5>{{ (item.trans_code && t(item.trans_code, ['menu_item']) !== item.trans_code) ? t(item.trans_code, ['menu_item']) : (item.name || item.trans_code) }} <span v-if="item.is_new" class="new-badge">{{ t('new', ['ordernow']) || 'New' }}</span></h5>
                    <p class="item-price">{{ formatDishPrice(item) }}</p>
                    <p class="item-description">{{ truncateDescription(t( item.trans_desc_code, ['menu_item'])) }}</p>
                  </div>
                  <div class="food-image-n-add-item">
                    <div class="position-relative">
                      <img class="" :src="getItemImage(item)" :alt="(item.trans_code && t(item.trans_code, ['menu_item']) !== item.trans_code) ? t(item.trans_code, ['menu_item']) : (item.name || '')" loading="lazy" width="300" height="300" :style="{ objectPosition: getItemFocal(item) }" />
                      <a href="" class="add-item-action" data-bs-toggle="modal" data-bs-target="#exampleModal" @click="openItemModal(item)">{{t('add', ['ordernow'])}}</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading Indicator for Infinite Scroll -->
            <div v-if="loading && !isInitialLoading" class="loading-indicator">
              <div class="d-flex justify-content-center align-items-center py-4">
                <div class="spinner-border text-primary me-3" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <span>{{t('loadin_more_items', ['ordernow'] ) || 'Loading more items...'}}</span>
              </div>
            </div>
            <div v-if="isAllItemsLoaded" class="end-of-items">
              <div class="text-center py-4 text-muted">
                <i class="fas fa-check-circle me-2"></i>
                {{t('all_items_loaded', ['ordernow'] ) || 'All items loaded'}}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cart -->
      <div class="col-xxl-2 col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 p-2 cart-desktop"        >
        <CartData />
      </div>
    </div>
  </div>

  <!-- cart-button-mobile -->
  <div class="mobile-cart" v-if="totalQty > 0">
      <button class="btn btn-primary" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasExample" aria-controls="offcanvasExample">
          <div class="view-cart-mobile">
              <span class="vcm-count"><span class="vcm-badge">{{ totalQty }}</span> {{t('items', ['ordernow'] )}}</span>
              <span class="vcm-action">{{t('view_cart', ['ordernow'] )}} <i class="fa-solid fa-arrow-right-long"></i></span>
          </div>
      </button>
  </div>

  <!-- item cart mobile popup -->
  <div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasExample" aria-labelledby="offcanvasExampleLabel">
      <div class="offcanvas-header">
          <!-- <h5 class="offcanvas-title" id="offcanvasExampleLabel">Your Cart</h5> -->
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
          <CartData />
      </div>
  </div>

  <!-- Success Toast Notification -->
  <div class="toast-container position-fixed top-0 end-0 p-3">
    <div id="successToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-header bg-success text-white">
        <i class="fas fa-check-circle me-2"></i>
        <strong class="me-auto">{{ t('toast_success', ['ordernow'], 'Success') }}</strong>
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">
        {{ t('item_added_to_cart', ['ordernow'], 'Item added successfully to cart!') }}
      </div>
    </div>
  </div>

  <!-- AddItem component removed here; global modal in App.vue will handle item details -->
</template>

<style>
.new-badge {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    background: #B6D452;
    color: #2f5018;
    border-radius: 999px;
    padding: 1px 8px;
    vertical-align: middle;
    margin-left: 6px;
}
:root {
    --body-color: #f1f0f5;
    --divider-color: #e4e4e4;
    --card-heading-color: #333333;
    --card-location-color: #787878;

    --darkest-green-bg-color: #576d27;
    --order-background-color: #76923b;
    --light-green-color: #b3c677;
    --dark-brown-color: #f00;
    --light-brown-colo: #c5886b;
    --lightest-brown-colo: #f7d9ba;

}

.outlet-card {
    background-color: white;
    border-radius: 0.5rem;
    display: flex;
    gap: 10px;
}

.search-btn {
    border-color: var(--order-background-color) !important;
    color: var(--order-background-color) !important;
    border-left: none !important;
}

.search-btn:hover, .search-btn:focus {
    background-color: var(--order-background-color) !important;
    color: white !important;
    border-color: var(--order-background-color) !important;
}

.input-group .form-control:focus {
    border-right: none;
    box-shadow: none;
}

.search-filter {
    background-color: white;
    border-radius: 0.5rem;
    vertical-align: middle;
}

.restaurent-img {
    border: 1px solid var(--divider-color);
    border-radius: 4px;

}

.restaurent-img img {
    width: 66px;
    height: 66px;
    object-fit: cover;
}

.outlet-detail h5 {
    font-size: 17px;
    margin-bottom: 0;
    color: var(--card-heading-color);
}

.outlet-detail p {
    font-size: 15px;
    margin-bottom: 0;
    color: var(--card-location-color);
}

.divider-heading {
    border-top: 1px solid var(--divider-color);
    width: 100%;
    height: 5px;
    margin-top: 2.5rem;
    margin-bottom: 2.5rem;
    position: relative;
}

.divider-heading h3 {
    position: absolute;
    text-transform: uppercase;
    top: -0.75rem;
    left: 50%;
    right: 0;
    color: var(--card-heading-color);
    background-color: var(--body-color) !important;
    width: max-content;
    padding: 0 10px;
    font-size: 20px;
    transform: translate(-50%, 0);
}

.card-item {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 6px rgb(0 0 0 / 8%);
    height: 100%;
    cursor: pointer;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
    border: 1px solid #f0f0ec;
}

.card-item:hover {
    box-shadow: 0 8px 20px rgb(0 0 0 / 12%);
    transform: translateY(-2px);
}

.card-item-food {
    display: flex;
    align-items: stretch;
    gap: 0.5rem;
    padding-bottom: 30px !important;
    height: 100%;
}

.food-item-detail {
    flex: 1 1 58%;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.food-image-n-add-item {
    flex: 0 0 40%;
    display: flex;
    align-items: flex-start;
}

.item-price {
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 8px;
    line-height: 15px;
    cursor: pointer;
}

.item-price small {
    color: #848484;
    font-size: 85%;
}

.food-item-detail h5 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
    color: var(--order-background-color);
    font-weight: 600;
    line-height: 1.3;
    /* wrap whole words, never split mid-word; cap at 2 lines */
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.6em;
}

.food-item-detail .item-description {
    font-size: 12px;
    color: var(--card-location-color);
    line-height: 1.45;
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.9em;
    margin-bottom: 0;
}

.food-image-n-add-item {
    position: relative;

}

.food-image-n-add-item img {
    width: 100%;
    aspect-ratio: 1/1;
    border-radius: 1rem;
    object-fit: cover;

}

.add-item-action {
    font-size: 16px;
    font-weight: 600;
    padding: 3px 12px;
    border: 1px solid var(--order-background-color);
    border-radius: 8px;
    width: max-content !important;
    text-align: center;
    position: absolute;
    background-color: white;
    position: absolute;
    bottom: -18px;
    left: 50%;
    transform: translate(-50%, 0%);
    text-decoration: none;
    color: var(--order-background-color)
}

.item-action {
    display: flex;
}

.item-action {
    position: absolute;
    bottom: -18px;
    left: 50%;
    transform: translate(-50%, 0%);
    color: white;
}

.item-action .subtract {
    padding: 5px;
    background-color: var(--order-background-color);
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    color: white;
    text-decoration: none;
    height: 32px;
}

.item-action p {
    padding: 5px;
    background-color: var(--order-background-color);
    margin-bottom: 0;
    height: 32px;
    width: 30px;
    text-align: center;
}

.item-action .add {
    padding: 5px;
    background-color: var(--order-background-color);
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    color: white;
    text-decoration: none;
    height: 32px;
}

.calories-detail {
    margin-top: 10px;
}

.calories-detail .overall-cal {
    background-color: #b3c677;
    width: 100%;
    display: block;
    text-align: center;
}

.ingridients-list {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem 1rem;
    flex-wrap: wrap;
}

.ingridients-list li {
    display: flex;
    align-items: center;
    flex-direction: column;
}

.ingridients-list li .nutreents {
    font-weight: 500;
    color: var(--card-location-color);
    font-size: 12px;
}

.ingridients-list li span:nth-child(2) {
    font-size: 13px;
}

.cart-section {
    position: sticky;
    position: -webkit-sticky;
    top: 122px;
    z-index: 1;
    background: #fff;
    padding: 10px 10px;
    border-radius: 10px 10px 0px 0px;
    height: calc(100vh - 128px);
}

.categories-section {
    position: sticky;
    position: -webkit-sticky;
    top: 122px;
    z-index: 1;
    background: #fff;
    border-radius: 10px 10px 0px 0px;
    height: calc(100vh - 128px);
    /* border-bottom: 1px solid var(--card-heading-color); */
}

/* --------------------cart ---------------- */
.cart-heading {
    font-weight: 600;
    font-size: 17px;
    margin-bottom: 15px;
    color: var(--card-heading-color)
}

.order-type-container {
    display: flex;
    padding: 10px 0;
    border: 1px solid var(--divider-color);
    padding: 4px;
    border-radius: 1rem;
}

.btn-active-order {
    background-color: #76923b;
    color: white;
    outline: none;
    padding: 10px 1rem;
    border: none;
    /* font-size: 12px; */
    border-radius: 12px;
    width: 33%;
}

.btn-active-order:hover {
    background-color: #76923b;
    color: white;
}

.btn-active-order:active {
    background-color: #76923b;
    color: white;
}

.btn-inactive-order {
    color: var(--card-heading-color);
    padding: 10px 1rem;
    border: none;
    background-color: transparent;
    font-size: 12px;
    width: 33%;
}

.btn-inactive-order:hover {
    color: var(--card-heading-color);
    border: none;
}

.btn-inactive-order:active {
    border: none;
    border-radius: 12px;
}

.orderl-list {
    max-height: 350px;
    padding-right: 10px;
    padding-left: 10px;
}

.order-item {
    display: flex;
    align-items: start;
    justify-content: space-between;
}

.order-item {
    display: flex;
    align-items: start;
    margin-bottom: 10px;
    border-bottom: 1px dashed var(--order-background-color);
    padding: 10px 0;
}

.order-item:last-child {
    border-bottom: none;
    margin-bottom: 0;
}

.delhivery-time {
    font-size: 12px;
    width: 100%;

}

.item-small-hd {
    font-size: 14px;
    margin-bottom: 3px;
    font-weight: 500;
    line-height: 20px;

}

.item-type-hd {
    font-size: 12px;
}

.item-price {
    margin-bottom: 10px;
    color: var(--dark-brown-color);
}

.item-price span {
    font-weight: 400;
    margin-right: 8px;
    font-size: 13px;
}

.item-action-cart {
    position: relative;
    bottom: 0
}

.item-action-cart a,
.item-action-cart p {
    background-color: white !important;
    color: var(--order-background-color) !important;
    border-color: var(--order-background-color);
    border-top: 1px solid;
    border-bottom: 1px solid;
}

.item-action-cart .add {
    border-right: 1px solid var(--order-background-color);
}

.item-action-cart .subtract {
    border-left: 1px solid var(--order-background-color);
}

.order-desscription {
    padding-right: 10px;
    margin-bottom: 0.5rem;
}

.final-subtotal {
    color: var(--order-background-color);
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-weight: 600;
}

/* ---------------- category sidebar (desktop) ---------------- */
.cat-sidebar {
    position: sticky;
    top: 100px;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 14px;
    padding: 14px 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.cat-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9aa08e;
    padding: 6px 12px 12px;
    margin: 0;
    line-height: 1.2;
}

.cat-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cat-link {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 11px 12px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14.5px;
    font-weight: 500;
    color: #3f4636;
    border-left: 3px solid transparent;
    transition: background 0.15s ease, color 0.15s ease;
}

.cat-link:hover {
    background: #f5f7ef;
}

.cat-link.active {
    background: #eef4e0;
    color: #3a5510;
    font-weight: 700;
    border-left: 3px solid #76923b;
}

.cat-ic {
    width: 22px;
    text-align: center;
    flex: 0 0 22px;
}

.cat-link .cat-ic i {
    color: #9bbb5f;
    font-size: 15px;
    transition: color 0.15s ease;
}

.cat-link.active .cat-ic i {
    color: #4c6b1a;
}

.cat-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ---------------- category chips (mobile) ---------------- */
/* Search + category chips stick just below the navbar while scrolling dishes */
.menu-sticky-top {
    position: -webkit-sticky;
    position: sticky;
    z-index: 30;
    background: var(--body-color, #f1f0f5);
    padding-top: 8px;
    box-shadow: 0 8px 12px -10px rgba(0, 0, 0, 0.25);
}

.cat-chips {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 2px 8px 14px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.cat-chips::-webkit-scrollbar {
    height: 0;
    display: none;
}

.cat-chips .chip {
    flex: 0 0 auto;
    border: 1px solid #dfe3d5;
    background: #fff;
    color: #4a503f;
    padding: 7px 15px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.cat-chips .chip.active {
    background: #576d27;
    color: #fff;
    border-color: #576d27;
}

/* category */
.category-item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    color: var(--card-heading-color);
    padding: 10px;
}

.category-item .order-count {
    font-weight: 600;
    color: var(--order-background-color);
}

.category-item .order-count p {
    margin-bottom: 0;
}

.category-item.active {
    background: linear-gradient(90deg, var(--order-background-color) 0%, #ffffff 100%);
    color: white;
    border-left: 3px solid #364e08;
}

/* .modal-food-item{
        max-width: 300px;
    } */

.modal-food-item .modal-content {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
}

.modal-food-item .modal-body {
    background-color: var(--body-color);
    padding: 0;
}

.btn-close-modal {
    position: absolute;
    top: 30px;
    right: 30px;
    background-color: white;
    padding: 10px;
    border-radius: 30px;
}

.item-img {
    background-color: white;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    padding: 1rem;
}

.item-img h5 {
    color: var(--order-background-color);
    font-size: 24px;
    font-weight: 700;
}

.item-img img {
    border-radius: 1rem;
    width: 100%
}

.price-text {
    font-size: 20px;
    color: var(--card-heading-color);
}

.meal-category {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    background-color: white
}

.extra-status {
    text-transform: uppercase;
    font-size: 13px;

    color: var(--card-location-color);
}

.toggle-button-extra {
    flex-direction: column;
    width: 33%;
    height: 100%;
}

.toggle-button-extra .extra-status {
    height: 30px;
}

.extra-options {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.text-status {
    color: var(--card-heading-color);
}

.nutrients-facts {
    color: var(--dark-brown-color);
    font-size: 1rem;
    font-weight: 700;
}

.meal-category-table table th {
    background-color: var(--dark-brown-color);
    color: white;
    text-align: center;
}

.meal-category-table table td {
    text-align: center;
}

.item-ippopup-action {
    position: relative;
    left: 0;
    bottom: 0;
    transform: translate(0, 0)
}

.item-ippopup-action a,
.item-ippopup-action p {
    border-top: 1px solid var(--order-background-color);
    border-bottom: 1px solid var(--order-background-color);
    background-color: white !important;
    color: var(--darkest-green-bg-color) !important;
    height: 38px !important;

}

.item-ippopup-action .subtract {
    border-left: 1px solid var(--order-background-color);

}

.item-ippopup-action .add {
    border-right: 1px solid var(--order-background-color);
}

.search-item-list {
    width: 100%;
    border-bottom: 0;
}

.search-item-list li {
    border-bottom: 1px dashed var(--card-location-color);
    width: 100%;
}

.search-item-list li:last-child {
    border: none;
}

.modal-search-dish .bg-modal-body-color {
    background-color: var(--body-color);
}

.searchitem-dish-image {
    width: 100px;
    height: 70px;
    object-fit: cover;
    border-radius: 10px;
}

.searchitem-dish-name {
    font-size: 1rem;
    color: var(--card-heading-color);
    padding-left: 1rem;
}

.mobile-cart {
    display: none;
}

.view-cart-mobile {
    display: flex;
    font-size: 18px;
    align-items: center;
    font-weight: 700;
    justify-content: space-between;
}

.btn-primary:disabled {
  background-color: var(--light-green-color) !important;
  border: 1px solid var(--light-green-color);
}
.btn-primary:disabled:hover {
  background-color: var(--light-green-color) !important;
}

@media screen and (max-width:991.91px) {
    .mobile-cart {
        display: block;
        position: fixed;
        bottom: 0;
        z-index: 99;
        padding: 10px;
        width: 100%;
    }

    .mobile-cart button {
        width: calc(100vw - 2rem);
        padding: 1rem;
    }

    .cart-desktop {
        display: none;
    }
}


/* Simple Loading Screen */
.simple-loading-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.simple-loading-content {
    text-align: center;
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.simple-loading-content .spinner-border {
    width: 3rem;
    height: 3rem;
    border-width: 0.3em;
}

.simple-loading-content h5 {
    color: var(--card-heading-color);
    margin-bottom: 0;
    font-weight: 600;
}

/* Loading Indicators */
.loading-indicator {
    width: 100%;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    margin: 1rem 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading-indicator .spinner-border {
    width: 1.5rem;
    height: 1.5rem;
    border-width: 0.2em;
}

.end-of-items {
    width: 100%;
    background: rgba(40, 167, 69, 0.1);
    border-radius: 10px;
    margin: 1rem 0;
    border: 1px solid rgba(40, 167, 69, 0.2);
}

.end-of-items i {
    color: #28a745;
}

@media screen and (max-width:600px) {
    .mobile-cart {
        display: block;
        position: fixed;
        bottom: 0;
        z-index: 99;
        padding: 10px;
        width: 100%;
    }

    .mobile-cart button {
        width: calc(100vw - 2rem);
        padding: 1rem;
    }

    .cart-desktop {
        display: none;
    }
    .food-item-detail h5{
        font-size: 20px;
    }
}

/* ===== Order Now polish (2026-07) ===== */
/* Cleaner cart bar: count badge left, action + arrow right (no redundant number) */
.view-cart-mobile .vcm-count { display: flex; align-items: center; gap: 9px; }
.view-cart-mobile .vcm-badge {
    background: var(--light-green-color, #B6D452); color: #23400f; border-radius: 999px;
    min-width: 27px; height: 27px; display: inline-flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 800; padding: 0 8px;
}
.view-cart-mobile .vcm-action { display: flex; align-items: center; gap: 9px; }
.mobile-cart .btn-primary { border-radius: 14px; box-shadow: 0 8px 22px rgba(40,60,25,0.3); }
/* App-like green "Add" button */
.add-item-action {
    background-color: #446936 !important; color: #fff !important; border-color: #446936 !important;
    box-shadow: 0 4px 12px rgba(40,60,25,0.28); transition: background .15s ease, transform .12s ease;
}
.add-item-action:hover { background-color: #38581c !important; color: #fff !important; }
.add-item-action:active { transform: translate(-50%, 0%) scale(0.95); }
.card-item:active { transform: scale(0.99); }
</style> 
